// src/index.jsx
import {jsx} from "@emotion/core";
import {Fragment, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import sanityClient from "@sanity/client";
import sanityImageUrl from "@sanity/image-url";
var SANITY_REF_PATTERN = /^image-([a-f\d]+)-(\d+x\d+)-(\w+)$/;
var DEFAULT_IMAGE_CONFIG = __GATSBY_PLUGIN_SANITY_IMAGE__DEFAULT_IMAGE_CONFIG__ || {
  auto: "format",
  fit: "max",
  quality: 75
};
var client2 = sanityClient({
  dataset: __GATSBY_PLUGIN_SANITY_IMAGE__DATASET__,
  projectId: __GATSBY_PLUGIN_SANITY_IMAGE__PROJECTID__,
  useCdn: __GATSBY_PLUGIN_SANITY_IMAGE__USECDN__
});
var builder = sanityImageUrl(client2);
var SanityImage = ({
  asset,
  hotspot,
  crop,
  width,
  height,
  options = {},
  config = {},
  __typename,
  _type,
  _key,
  sources,
  ...props
}) => {
  const preview = asset.metadata?.preview || asset.metadata?.lqip;
  if (__GATSBY_PLUGIN_SANITY_IMAGE__ALT_FIELD__) {
    props.alt = props.alt ?? asset[__GATSBY_PLUGIN_SANITY_IMAGE__ALT_FIELD__];
  }
  asset = {
    _id: asset._id || asset._ref,
    hotspot,
    crop
  };
  if (parseImageRef(asset._id).format === "svg") {
    return /* @__PURE__ */ jsx("img", {
      src: imageUrl(asset),
      ...props
    });
  }
  const src = buildSrc(asset, {...config, width, height});
  const srcSet = buildSrcSet(asset, {...config, width, height});
  if (options.__experimentalAspectRatio) {
    const {dimensions} = parseImageRef(asset._id);
    crop = crop || {left: 0, right: 0, top: 0, bottom: 0};
    const croppedWidth = dimensions.width * (1 - crop.left - crop.right);
    const croppedHeight = dimensions.height * (1 - crop.top - crop.bottom);
    const ratio = croppedWidth / croppedHeight;
    props.width = width || dimensions.width;
    props.height = Math.round(props.width / ratio);
  }
  const Image = preview ? ImageWithPreview : "img";
  return /* @__PURE__ */ jsx(Image, {
    preview,
    src,
    srcSet,
    css: hotspot && {
      objectPosition: [hotspot.x, hotspot.y].map((value) => (value * 100).toFixed(2) + "%").join(" ")
    },
    loading: "lazy",
    ...props
  });
};
var src_default = SanityImage;
var buildSrc = (asset, {width, height, ...config}) => {
  const {dimensions} = parseImageRef(asset._id);
  const origRatio = dimensions.width / dimensions.height;
  width = width || dimensions.width;
  height = height || Math.round(width / origRatio);
  return imageUrl(asset, {...config, width, height});
};
var buildSrcSet = (asset, config) => {
  const {dimensions} = parseImageRef(asset._id);
  const fitMode = config.fit || DEFAULT_IMAGE_CONFIG.fit;
  const origRatio = dimensions.width / dimensions.height;
  const width = config.width || dimensions.width;
  const height = config.height || Math.round(width / origRatio);
  const targetRatio = width / height;
  let cropRatio = origRatio;
  let maxWidth = dimensions.width;
  let maxHeight = dimensions.height;
  if (asset.crop && Object.values(asset.crop).some((n) => n > 0)) {
    const cropWidth = dimensions.width - asset.crop.left * dimensions.width - asset.crop.right * dimensions.width;
    const cropHeight = dimensions.height - asset.crop.top * dimensions.height - asset.crop.bottom * dimensions.height;
    cropRatio = cropWidth / cropHeight;
    if (cropRatio > origRatio) {
      maxHeight = cropHeight;
    } else {
      maxWidth = cropWidth;
    }
  }
  return Object.values([0.5, 0.75, 1, 1.5, 2].reduce((set, dpr) => {
    const url = imageUrl(asset, {...config, dpr});
    const size = Math.round(["fillmax", "max", "min"].includes(fitMode) ? targetRatio < origRatio ? Math.min(maxHeight / (height * dpr) * (width * dpr), width * dpr) : Math.min(width * dpr, maxWidth) : width * dpr);
    if (!set.size) {
      set[size] = `${url} ${size}w`;
    }
    return set;
  }, {}));
};
var ImageWithPreview = ({preview, ...props}) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef();
  const onLoad = () => {
    setLoaded(true);
  };
  useEffect(() => {
    if (ref.current && ref.current.complete) {
      onLoad();
    }
  });
  return /* @__PURE__ */ jsx(Fragment, null, !loaded && /* @__PURE__ */ jsx("img", {
    src: preview,
    alt: props.alt,
    id: props.id,
    className: props.className,
    style: props.style,
    width: props.width,
    height: props.height,
    "data-lqip": true
  }), /* @__PURE__ */ jsx("img", {
    ref,
    onLoad,
    css: !loaded && {
      position: "absolute",
      width: "10px !important",
      height: "10px !important",
      opacity: 0,
      zIndex: -10,
      pointerEvents: "none",
      userSelect: "none"
    },
    "data-loading": loaded ? null : true,
    ...props
  }));
};
ImageWithPreview.propTypes = {
  preview: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  alt: __GATSBY_PLUGIN_SANITY_IMAGE__ALT_FIELD__ ? PropTypes.string : PropTypes.string.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number
};
SanityImage.propTypes = {
  config: PropTypes.object,
  options: PropTypes.shape({
    __experimentalAspectRatio: PropTypes.bool
  }),
  hotspot: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number
  }),
  crop: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number
  }),
  asset: PropTypes.oneOfType([
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        preview: PropTypes.string,
        lqip: PropTypes.string
      })
    }),
    PropTypes.shape({
      _ref: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        preview: PropTypes.string,
        lqip: PropTypes.string
      })
    })
  ]).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  alt: __GATSBY_PLUGIN_SANITY_IMAGE__ALT_FIELD__ ? PropTypes.string : PropTypes.string.isRequired,
  className: PropTypes.string,
  sizes: PropTypes.string,
  __typename: PropTypes.any,
  _type: PropTypes.any,
  _key: PropTypes.any,
  sources: PropTypes.any
};
var parseImageRef = (id) => {
  try {
    const [, assetId, dimensions, format] = SANITY_REF_PATTERN.exec(id);
    const [width, height] = dimensions.split("x").map((v) => parseInt(v, 10));
    return {
      assetId,
      dimensions: {width, height},
      format
    };
  } catch {
    throw new Error(`Could not parse image ID "${id}"`);
  }
};
var imageUrl = (asset, params = {}) => Object.entries({...DEFAULT_IMAGE_CONFIG, ...params}).reduce((acc, [key, value]) => value ? Array.isArray(value) ? acc[key](...value) : acc[key](value) : acc, builder.image(asset)).url();
export {
  DEFAULT_IMAGE_CONFIG,
  SANITY_REF_PATTERN,
  builder,
  client2 as client,
  src_default as default,
  imageUrl,
  parseImageRef
};
